import * as React from 'react';
import { CanvasUtils } from '@shared/utils/CanvasUtils';
import { observer } from 'mobx-react';
import { makeObservable, observable, reaction } from 'mobx';
import { Notify } from '@shared/ui-native/Notify';
import { LogpointFeatureFactory } from '@openlayers/logpoints/LogpointFeatureFactory';
import { LogpointsService } from '@shared/logpoints/LogpointsService';
import { ProjectService } from '@shared/services/ProjectService';

import { getCompositeMapCanvas, getHighResMapCanvas } from '@openlayers/ol-utils';
import OlMap from 'ol/Map';
import { transform } from 'ol/proj';
import { cs } from '@shared/utils/utils-classname';
import ReactCrop, { type Crop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IUser } from '@pages/mobile/ui/panels/ContactsPanel';
import { UsersService } from '@shared/services/UsersService';
import { ReactIcons } from '@shared/ReactIcons';
import { OrganizationService } from '@shared/services/OrganizationService';
import * as canvasTxt from 'canvas-txt';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { LStorage } from '@shared/Storage';
import { Button } from '@shared/ui-react/Button';
import { DateTimePicker } from '@shared/ui-react/DateTimePicker';
import { Tooltip } from 'primereact/tooltip';
import { Extent } from 'ol/extent';
import { measurementStore } from '@shared/openlayers/tools/volume/utils/store/measurement-store';

const compassImg =
    'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTUwIiBoZWlnaHQ9IjE1MCIgdmlld0JveD0iMCAwIDE1MCAxNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGQ9Ik03NS4wMDAzIDkuMzc0MThMNTYuMjUwMSA3NC45OTk5SDkzLjc1MDVMNzUuMDAwMyA5LjM3NDE4WiIgZmlsbD0iI0ZGODIzRCIvPgo8cGF0aCBkPSJNNTYuMjUwMSA3NC45OTk5TDc1LjAwMDMgMTQwLjYyNkw5My43NTA1IDc0Ljk5OTlINTYuMjUwMVoiIGZpbGw9IiM1NTVDNjQiLz4KPHBhdGggZD0iTTc4LjEyNTMgNzQuOTk5OUM3OC4xMjUzIDc2LjcyNTggNzYuNzI2MiA3OC4xMjQ5IDc1LjAwMDMgNzguMTI0OUM3My4yNzQ0IDc4LjEyNDkgNzEuODc1MyA3Ni43MjU4IDcxLjg3NTMgNzQuOTk5OUM3MS44NzUzIDczLjI3NCA3My4yNzQ0IDcxLjg3NDkgNzUuMDAwMyA3MS44NzQ5Qzc2LjcyNjIgNzEuODc0OSA3OC4xMjUzIDczLjI3NCA3OC4xMjUzIDc0Ljk5OTlaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTc1LjAxMTMgOC4xNDM1NUw5NC4wNzMyIDc1LjAyNTZMNzUuMDExMyAxNDEuMTJMNTYuMDcxMyA3NS4wMjU2TDc1LjAxMTMgOC4xNDM1NVpNNzUuMDIxNiAxMC40NTk4TDU2LjgyODEgNzUuMDI0Nkw3NS4wMjE2IDEzOC44MjlMOTMuNDIzMSA3NS4wMjQ2TDc1LjAyMTYgMTAuNDU5OFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=';

@observer
export class SaveMapImageDialog extends React.Component<{
    map: OlMap;
    mapLabels: any[];
    measureLabels: any[];
    logpointsColorMode: LogpointColorFunction;
    mapBounds: Extent;
    mapDegrees: number;
}> {
    public readonly storageKey = kuura.getProjectUuid() + ' _save_map_image_dialog';
    @observable isLoading = false;
    @observable includeModelNames = true;
    @observable includeScaleLine = true;
    @observable includeLegend = true;
    @observable legendMode: 'logpoints' | 'mass' = 'logpoints';
    @observable includeTime = true;
    @observable exportDateMs = Date.now();
    @observable paperType: string = 'free'; // free, A4, A5, or custom aspect ratio
    @observable aspectRatio: string = 'free'; // free or the aspect ratio of the paper
    @observable paperLabel: string = ''; // Label to use in the print
    @observable papersize = {
        height: 0, // in mm
        width: 0, // in mm
    };
    @observable groundSize = {
        height: 0, // in m
        width: 0, // in m
    };
    cropRealSize = {
        x: 0,
        y: 0,
        height: 0,
        width: 0,
    };
    @observable scale = 0;
    @observable overlayScale = 1;
    @observable resolutionScale = 1;
    @observable isRendering = false;
    @observable.ref previewCanvas: HTMLCanvasElement = null;
    @observable.ref previewCanvasB64: string = null;
    @observable.ref originalCanvas: HTMLCanvasElement = null;
    @observable.ref crop: Crop = null;
    @observable project: Partial<IProject> = {};
    @observable user: Partial<IUser> = {};

    @observable customFields = [];
    public readonly customFieldsDefault = [{ label: __('general.description'), value: '' }];

    logoImg: HTMLImageElement = null;
    mapLabelFont = '20px Arial, Helvetica, sans-serif';

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetchData();

        const storedSettings = LStorage.getOrDefault(this.storageKey, null);
        this.customFields = storedSettings?.customFields ?? this.customFieldsDefault;
        if (storedSettings) {
            this.includeTime = !!storedSettings.includeTime;
            this.includeModelNames = !!storedSettings.includeModelNames;
            this.includeLegend = !!storedSettings.includeLegend;
            if (!storedSettings.legendMode) {
                this.legendMode = null;
            } else {
                if (this.props.measureLabels?.some(lbl => lbl.type === 'volume')) {
                    this.legendMode = 'mass';
                } else {
                    this.legendMode = 'logpoints';
                }
            }
            if (storedSettings.scale >= 0.5 && storedSettings.scale <= 3) {
                this.overlayScale = storedSettings.scale ?? 1;
            }
        }
        reaction(() => [this.includeTime, this.includeModelNames, this.includeLegend, this.legendMode], this.saveSettings);
    }

    async fetchData() {
        this.isLoading = true;
        try {
            this.project = await ProjectService.get(kuura.getProjectUuid());
            this.user = await UsersService.getByUuid(kuura.getUserUuid());
            this.isLoading = false;
            this.updatePreview();
        } catch {
            this.isLoading = false;
            Notify.error(__('js_files.msg.notifications.error_loading_dialog'));
        }
    }

    render() {
        if (!this.previewCanvas) return <div style={{ height: 200 }} className='overlay-loading' />;

        return (
            <div className='flex-row gap-half'>
                <div className='padded flex-column gap centered'>
                    <Tabs displayValue='flex' flexDirection='column'>
                        <TabPane title={__('js_map.image_export.basic_settings')}>
                            <div style={{ marginLeft: -5 }}>
                                <label className='checkbox-label'>
                                    <input
                                        type='checkbox'
                                        checked={this.includeModelNames}
                                        onClick={() => {
                                            this.includeModelNames = !this.includeModelNames;
                                            this.updatePreview();
                                        }}
                                        readOnly
                                    />
                                    <span>{__('js_map.image_export.include_model_names')}</span>
                                </label>
                            </div>

                            <div style={{ marginTop: 10 }}>
                                <label className='styled'>{__('general.aspect_ratio')}</label>
                                <select
                                    value={this.paperType}
                                    onChange={e => this.ChangeSelectedPaper(e.currentTarget.value)}
                                    ref={select => {
                                        if (select && !select.dataset.mounted) {
                                            select.dataset.mounted = 'true';
                                            this.ChangeSelectedPaper(this.paperType);
                                        }
                                    }}
                                >
                                    <option value='free'>{__('general.aspect_ratio.free')}</option>
                                    <option value='A3'>A3</option>
                                    <option value='A4'>A4</option>
                                    <option value='1'>1:1</option>
                                    <option value='1.7777'>16:9</option>
                                </select>
                            </div>

                            <div style={{ marginTop: 10 }}>
                                <label className='styled'>{__('js_map.image_export.overlay_scale')}</label>
                                <Button primary={this.overlayScale === 1} onClick={() => this.setOverlayScale(1)}>
                                    100%
                                </Button>
                                <Button primary={this.overlayScale === 1.5} onClick={() => this.setOverlayScale(1.5)}>
                                    75%
                                </Button>
                                <Button primary={this.overlayScale === 2} onClick={() => this.setOverlayScale(2)}>
                                    50%
                                </Button>
                            </div>

                            <div style={{ marginTop: 10 }}>
                                <Tooltip target='.resolution-scale-tooltip' baseZIndex={99999} position='top' content={__('js_map.image_export.resolution_scale_warning')} />
                                <label className='styled'>
                                    {__('js_map.image_export.resolution_scale')}&nbsp;
                                    <i className='iki iki-info resolution-scale-tooltip' style={{ fontSize: '1.2em', top: 3, position: 'relative' }}></i>
                                </label>

                                <Button primary={this.resolutionScale === 1} onClick={() => this.setResolutionScale(1)}>
                                    100%
                                </Button>
                                <Button primary={this.resolutionScale === 1.5} onClick={() => this.setResolutionScale(1.5)}>
                                    150%
                                </Button>
                                <Button primary={this.resolutionScale === 2} onClick={() => this.setResolutionScale(2)}>
                                    200%
                                </Button>
                            </div>
                        </TabPane>

                        <TabPane title={__('js_map.image_export.info_overlay')}>
                            <label className='checkbox-label'>
                                <input
                                    type='checkbox'
                                    checked={this.includeLegend}
                                    onClick={() => {
                                        this.includeLegend = !this.includeLegend;
                                        this.updatePreview();
                                    }}
                                    readOnly
                                />
                                <span>{__('js_map.image_export.enable_info_overlay')}</span>
                            </label>

                            <div className={cs({ 'fade-disable': !this.includeLegend })}>
                                <label className='styled' style={{ marginTop: 20 }}>
                                    {__('js_map.image_export.legend_type')}
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type='radio'
                                        name='legendMode'
                                        checked={this.legendMode === 'logpoints'}
                                        onChange={() => {
                                            this.legendMode = 'logpoints';
                                            this.updatePreview();
                                        }}
                                    />
                                    <span>{__('js_map.image_export.logpoints_legend')}</span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type='radio'
                                        name='legendMode'
                                        checked={this.legendMode === 'mass'}
                                        onChange={() => {
                                            this.legendMode = 'mass';
                                            this.updatePreview();
                                        }}
                                    />
                                    <span>{__('js_map.image_export.measurements_legend')}</span>
                                </label>
                                <label className='checkbox-label'>
                                    <input
                                        type='radio'
                                        name='legendMode'
                                        checked={this.legendMode === null}
                                        onChange={() => {
                                            this.legendMode = null;
                                            this.updatePreview();
                                        }}
                                    />
                                    <span>{__('general.none')}</span>
                                </label>

                                <div style={{ marginTop: 15 }}>
                                    <label className='styled'>{__('general.export_date')}</label>
                                    <DateTimePicker
                                        showTimeSelect={true}
                                        value={this.exportDateMs}
                                        onChange={ms => {
                                            this.exportDateMs = ms;
                                            this.updatePreviewDebounced();
                                        }}
                                    />
                                    <label className='checkbox-label'>
                                        <input
                                            type='checkbox'
                                            checked={this.includeTime}
                                            onClick={() => {
                                                this.includeTime = !this.includeTime;
                                                this.updatePreview();
                                            }}
                                            readOnly
                                        />
                                        <span>{__('js_map.image_export.include_time')}</span>
                                    </label>
                                </div>

                                <label style={{ marginTop: 15 }} className='styled'>
                                    {__('js_map.image_export.custom_fields')}
                                </label>
                                <table style={{ width: '300px' }}>
                                    <tbody>
                                        {this.customFields.map((field, index) => (
                                            <tr key={index}>
                                                <td>
                                                    <input
                                                        type='text'
                                                        placeholder={__('general.name')}
                                                        value={field.label}
                                                        onChange={e => this.setCustomFieldLabel(index, e.target.value)}
                                                        maxLength={50}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type='text'
                                                        placeholder={__('general.value')}
                                                        value={field.value}
                                                        onChange={e => this.setCustomFieldValue(index, e.target.value)}
                                                        maxLength={200}
                                                    />
                                                </td>
                                                <td>
                                                    <Button subtle secondary icon='pi pi-times' onClick={() => this.removeCustomField(index)} />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        {this.customFields?.length <= 4 && (
                                            <tr>
                                                <td colSpan={3} className='text-right'>
                                                    <Button onClick={this.addCustomField}>{__('general.add')}</Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tfoot>
                                </table>
                            </div>
                        </TabPane>
                    </Tabs>

                    <div style={{ marginTop: 'auto' }}>
                        <label className='styled text-center'>{__('general.download')}</label>
                        <div className='flex-row centered'>
                            <button onClick={() => this.renderAndSave('pdf')} className='ik-button-large ik-button-secondary--subtle'>
                                {ReactIcons.pdf} <span>PDF</span>
                            </button>
                            <div style={{ width: 1, backgroundColor: '#DCE3E4', margin: '0 5px', height: '1.7em' }}></div>
                            <button onClick={() => this.renderAndSave('jpeg')} className='ik-button-large ik-button-secondary--subtle'>
                                {ReactIcons.jpeg} <span>JPEG</span>
                            </button>
                            <div style={{ width: 1, backgroundColor: '#DCE3E4', margin: '0 5px', height: '1.7em' }}></div>
                            <button onClick={() => this.renderAndSave('png')} className='ik-button-large ik-button-secondary--subtle'>
                                {ReactIcons.png} <span>PNG</span>
                            </button>
                        </div>
                    </div>
                </div>
                <div className={cs('flex-row', { 'overlay-loading': this.isRendering })}>
                    <ReactCrop
                        minWidth={100}
                        minHeight={100}
                        crop={this.crop}
                        onChange={c => (this.crop = c)}
                        onComplete={this.onPreviewCropCompleteDebounced}
                        aspect={this.aspectRatio !== 'free' ? parseFloat(this.aspectRatio) : null}
                    >
                        <img style={{ height: 600, pointerEvents: 'none' }} src={this.previewCanvasB64} />
                    </ReactCrop>
                </div>
            </div>
        );
    }

    setCustomFieldLabel = (index: number, label: string) => {
        this.customFields[index].label = label;
        this.saveSettings();
        this.updatePreviewDebounced();
    };

    setCustomFieldValue = (index: number, value: string) => {
        this.customFields[index].value = value;
        this.saveSettings();
        this.updatePreviewDebounced();
    };

    removeCustomField = (index: number) => {
        this.customFields.splice(index, 1);
        this.saveSettings();
        this.updatePreviewDebounced();
    };

    addCustomField = () => {
        this.customFields.push({ label: '', value: '' });
        this.saveSettings();
        this.updatePreviewDebounced();
    };

    setOverlayScale = (scale: number) => {
        this.overlayScale = scale;
        this.saveSettings();
        this.updatePreviewDebounced();
    };

    setResolutionScale = (resolutionScale: number) => {
        this.resolutionScale = resolutionScale;
        this.originalCanvas = null;
        this.updatePreview();
    };

    updatePreviewDebounced = _.throttle(() => {
        this.updatePreview();
    }, 100);

    async updatePreview() {
        this.isRendering = true;
        if (!this.originalCanvas) {
            if (this.resolutionScale === 1) {
                this.originalCanvas = getCompositeMapCanvas(this.props.map, true);
            } else {
                this.originalCanvas = await getHighResMapCanvas(this.props.map, true, this.resolutionScale);
            }
            this.crop = null;
            this.cropRealSize.x = 0;
            this.cropRealSize.y = 0;
            this.cropRealSize.width = this.originalCanvas.width;
            this.cropRealSize.height = this.originalCanvas.height;
        }
        this.previewCanvas = await this.renderFinalCanvas();
        this.previewCanvasB64 = this.previewCanvas.toDataURL('image/jpeg', 0.5);
        this.isRendering = false;
    }

    onPreviewCropCompleteDebounced = _.debounce((crop: Crop, percentage) => {
        this.onPreviewCropComplete(crop, percentage);
    }, 100);

    onPreviewCropComplete = (crop: Crop, percentage) => {
        if (!crop) return;
        if (crop.height <= 5 || crop.width <= 5) {
            crop = {
                unit: '%',
                x: 0,
                y: 0,
                width: 100,
                height: 100,
            };
            percentage = {
                x: 0,
                y: 0,
                height: 100,
                width: 100,
            };
        }
        this.crop = crop;
        this.cropRealSize = {
            x: this.originalCanvas.width * (percentage.x / 100),
            y: this.originalCanvas.height * (percentage.y / 100),
            height: this.originalCanvas.height * (percentage.height / 100),
            width: this.originalCanvas.width * (percentage.width / 100),
        };
        this.updatePreviewDebounced();
    };

    HandleKnownPaper = (paperName: string) => {
        // If dimensions of the paper are known run this function
        this.paperType = paperName;

        let height: number, width: number;
        if (paperName === 'A3') {
            // A3 is 297mm x 420mm
            width = 297;
            height = 420;
        } else if (paperName === 'A4') {
            // A4 is 297mm x 210mm
            width = 210;
            height = 297;
        }

        // Store paper dimensions
        this.papersize = {
            height: height, // in mm
            width: width, // in mm
        };

        // Calculate aspect ratio from dimensions
        this.aspectRatio = (height / width).toString();
        this.onChangeAspectRatio(this.aspectRatio);

        // Use paperName as label
        this.paperLabel = paperName;
    };

    HandleOtherPaper = () => {
        // Calculate aspect ratio of the cropped canvas
        let aspectRatio: number;
        if (this.cropRealSize.width === 0 || this.cropRealSize.height === 0) {
            // If there is no crop on canvas use aspect ratio of the original canvas
            aspectRatio = this.originalCanvas.width / this.originalCanvas.height;
        } else {
            // Otherwise use aspect ratio of the cropped canvas
            aspectRatio = this.cropRealSize.width / this.cropRealSize.height;
        }
        console.log('Aspect ratio:', aspectRatio);

        // Use A4 papersize by default. TODO consider if we could let user to determine
        let height = 297; // in mm
        let width = height / aspectRatio;

        this.papersize = {
            height: height, // in mm
            width: width, // in mm
        };

        // Use papersize as label
        this.paperLabel = `${width.toFixed(0)}mm x ${height.toFixed(0)}mm`;
    };

    onChangeAspectRatio = ratio => {
        this.aspectRatio = ratio;
        if (ratio !== 'free') {
            this.crop = makeAspectCrop(
                {
                    unit: '%',
                    width: 100,
                },
                parseFloat(ratio),
                this.originalCanvas.width,
                this.originalCanvas.height
            );
            this.onPreviewCropComplete(this.crop, this.crop);
        }
    };

    calculateScale = () => {
        // Calculate width and height of the openlayers map
        const centerEn = [(this.props.mapBounds[0] + this.props.mapBounds[2]) / 2, (this.props.mapBounds[1] + this.props.mapBounds[3]) / 2];
        const latitude = transform(centerEn, 'EPSG:3857', 'EPSG:4326')[1];
        const mercatorCorrectionFactor = 1 / Math.cos((latitude * Math.PI) / 180);
        console.log('Latitude:', latitude, 'Mercator correction factor:', mercatorCorrectionFactor);

        // Convert map bounds to meters
        const trueWidth = (this.props.mapBounds[2] - this.props.mapBounds[0]) / mercatorCorrectionFactor;
        const trueHeight = (this.props.mapBounds[3] - this.props.mapBounds[1]) / mercatorCorrectionFactor;
        console.log('True width:', trueWidth);
        console.log('True height:', trueHeight);

        // Determine how much the map is cropped
        const widthCropFactor = this.cropRealSize.width / this.originalCanvas.width;
        const heightCropFactor = this.cropRealSize.height / this.originalCanvas.height;

        // Calculate the real life width and height of the cropped map in meters
        const cropTrueWidth = trueWidth * widthCropFactor;
        const cropTrueHeight = trueHeight * heightCropFactor;

        // Calculate scale. Papersize in mm, true width and height in m.
        // Map is mounted horizontally, so we need to swap width and height for scale calculation
        const scaleX = cropTrueWidth / (this.papersize.height / 1000);
        const scaleY = cropTrueHeight / (this.papersize.width / 1000);

        this.scale = Math.min(scaleX, scaleY);
    };

    ChangeSelectedPaper = (paperType: string) => {
        console.log('Selected paper type:', paperType);
        if (paperType === 'A3' || paperType === 'A4') {
            this.HandleKnownPaper(paperType);
        } else {
            this.paperType = paperType;
            this.onChangeAspectRatio(paperType);
            this.HandleOtherPaper();
        }
        this.calculateScale();
    };

    renderAndSave = async (fileFormat: 'png' | 'jpeg' | 'pdf') => {
        const finalCanvas = await this.renderFinalCanvas();
        let destCanvas = document.createElement('canvas');
        destCanvas.width = this.cropRealSize.width;
        destCanvas.height = this.cropRealSize.height;
        destCanvas
            .getContext('2d')
            .drawImage(
                finalCanvas,
                this.cropRealSize.x,
                this.cropRealSize.y,
                this.cropRealSize.width,
                this.cropRealSize.height,
                0,
                0,
                this.cropRealSize.width,
                this.cropRealSize.height
            );

        const filename = `Infrakit map - ${kuura.getProjectName()} - ${moment().format('YYYY-MM-DD LT')}`;
        await CanvasUtils.saveToFile(destCanvas, filename, fileFormat);
    };

    renderFinalCanvas = async () => {
        this.isLoading = true;
        const overlayCanvas = document.createElement('canvas');
        const dpr = window.devicePixelRatio * this.resolutionScale;

        try {
            const ctx = overlayCanvas.getContext('2d');
            overlayCanvas.width = this.cropRealSize.width * this.effectiveOverlayScale;
            overlayCanvas.height = this.cropRealSize.height * this.effectiveOverlayScale;

            // logo
            if (!this.logoImg) {
                this.logoImg = await OrganizationService.fetchOrganizationLogo();
            }
            const logoRatio = this.logoImg.naturalWidth / this.logoImg.naturalHeight;
            const logoWidth = 250;
            ctx.drawImage(this.logoImg, 20, 20, logoWidth, logoWidth / logoRatio);
            ctx.font = ctx.font.replace(/\d+px/, 15 * dpr + 'px');

            ctx.textAlign = 'start';
            if (this.includeLegend) {
                const boxWidth = 400;
                const boxHeight = 230;
                this.drawApprovedBox(overlayCanvas, overlayCanvas.width - boxWidth * 2, overlayCanvas.height - boxHeight, this.user);
                this.drawScaleDateBox(overlayCanvas, overlayCanvas.width - boxWidth, overlayCanvas.height - boxHeight, this.project);

                if (this.legendMode === 'mass') {
                    this.drawMassCalculationBox(overlayCanvas, overlayCanvas.width - boxWidth, overlayCanvas.height - (400 + boxHeight));
                } else if (this.legendMode === 'logpoints') {
                    this.drawLogpointsBox(overlayCanvas, overlayCanvas.width - boxWidth, overlayCanvas.height - (400 + boxHeight));
                }

                // call these last because canvasTxt will mess up the context and shift all other text
                const customFieldsWithProject = [{ label: __('general.project'), value: kuura.getProjectName() }, ...this.customFields];
                const customFieldPairs = _.chunk(customFieldsWithProject, 2);
                customFieldPairs.forEach((pair, index) => {
                    this.drawCustomInfoBox(
                        overlayCanvas,
                        overlayCanvas.width - boxWidth * (3 + index),
                        overlayCanvas.height - boxHeight,
                        pair[0]?.label,
                        pair[0]?.value,
                        pair[1]?.label,
                        pair[1]?.value
                    );
                });

                // draw compass
                ctx.save();
                ctx.translate(overlayCanvas.width - 100, 80 + 50 * this.effectiveOverlayScale);
                ctx.rotate((this.props.mapDegrees * Math.PI) / 180);
                ctx.translate(-60, -60);
                ctx.drawImage(await CanvasUtils.makeImage(compassImg), 0, 0);
                ctx.restore();
            }
        } catch (e) {
            console.log('e:', e);
            Notify.error();
        } finally {
            this.isLoading = false;
        }

        const compositeCanvas = document.createElement('canvas');
        const compCtx = compositeCanvas.getContext('2d');
        compositeCanvas.width = this.originalCanvas.width;
        compositeCanvas.height = this.originalCanvas.height;
        compCtx.fillStyle = 'white';
        compCtx.fillRect(0, 0, overlayCanvas.width, overlayCanvas.height);
        compCtx.drawImage(this.originalCanvas, 0, 0);

        // draw measuring tool labels (they are divs and not printed on canvas)
        const scaledLabels = [...this.props.measureLabels, ...(this.includeModelNames ? this.props.mapLabels : [])].map(l => ({
            ...l,
            center: [l.center[0] * this.resolutionScale, l.center[1] * this.resolutionScale],
        }));
        const allLabels = this.includeModelNames ? this.layoutLabels(scaledLabels, compCtx, window.devicePixelRatio) : [];
        allLabels.forEach(l => {
            compCtx.fillStyle = 'black';
            compCtx.textAlign = l.align;
            compCtx.font = this.mapLabelFont;
            const labelDpr = window.devicePixelRatio;
            wrapText(compCtx, l.name, l.center[0] * labelDpr, (l.center[1] - 30) * labelDpr, 300 * labelDpr, 24, false);
        });

        // draw scale line
        const scaleLineX = compositeCanvas.width - 100;
        const scaleLineY = 20;
        const lineOffset = 70;

        const scaleEL = document.querySelector('.ol-scale-line-inner') as HTMLDivElement;
        const scaleLineW = 2;
        const width = scaleEL.offsetWidth * dpr;
        compCtx.fillStyle = 'black';
        compCtx.fillRect(scaleLineX - width + lineOffset, scaleLineY + 20, width, scaleLineW);
        compCtx.fillRect(scaleLineX - width + lineOffset, scaleLineY, scaleLineW, 20);
        compCtx.fillRect(scaleLineX + scaleLineW + lineOffset, scaleLineY, scaleLineW, 20);
        drawText(compCtx, scaleEL.innerText, scaleLineX + lineOffset - width / 2, scaleLineY + 10, 18);

        compCtx.drawImage(overlayCanvas, this.cropRealSize.x, this.cropRealSize.y, this.cropRealSize.width, this.cropRealSize.height);

        return compositeCanvas;
    };

    layoutLabels = (labels, ctx: CanvasRenderingContext2D, dpr: number) => {
        if (!labels || labels.length === 0) return labels;

        const result = JSON.parse(JSON.stringify(labels));
        const boundingBoxes = result.map(label => this.calculateLabelBoundingBox(label, ctx, dpr));

        for (let i = 1; i < result.length; i++) {
            let currentBox = boundingBoxes[i];

            let hasOverlap = false;
            for (let j = 0; j < i; j++) {
                if (this.doBoxesOverlap(currentBox, boundingBoxes[j])) {
                    hasOverlap = true;
                    break;
                }
            }
            if (!hasOverlap) continue;

            let shiftDistance = 10;
            let attempt = 0;
            const origCenter = result[i].center[1];

            while (hasOverlap && attempt < 50) {
                // try for empty space up/down, switching on every attempt
                attempt++;
                if (attempt % 2 === 0) {
                    shiftDistance += 10;
                }
                result[i].center[1] = origCenter + shiftDistance * (attempt % 2 ? 1 : -1);
                const newBox = this.calculateLabelBoundingBox(result[i], ctx, dpr);

                hasOverlap = false;
                for (let j = 0; j < i; j++) {
                    if (this.doBoxesOverlap(newBox, boundingBoxes[j])) {
                        hasOverlap = true;
                        break;
                    }
                }

                if (!hasOverlap) {
                    boundingBoxes[i] = newBox;
                }
            }
        }

        return result;
    };

    calculateLabelBoundingBox = (label, ctx: CanvasRenderingContext2D, dpr: number) => {
        ctx.font = this.mapLabelFont;
        const metrics = ctx.measureText(label.name);
        const textWidth = Math.min(metrics.width, 300 * dpr);
        const lineHeight = 15;
        const lines = Math.ceil(metrics.width / (300 * dpr));
        const fontHeight = metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent;
        const textHeight = Math.max(fontHeight, lines * lineHeight);

        let x = label.center[0] * dpr;
        if (label.align === 'center') {
            x -= textWidth / 2;
        } else if (label.align === 'right') {
            x -= textWidth;
        }

        const y = (label.center[1] - 30) * dpr - textHeight;
        const padding = 2;

        // debug box
        //ctx.strokeStyle = 'red';
        //ctx.lineWidth = 2;
        //ctx.strokeRect(x - padding, y - padding, textWidth + padding * 2, textHeight + padding * 2);

        return {
            x: x - padding,
            y: y - padding,
            width: textWidth + padding * 2,
            height: textHeight + padding * 2,
        };
    };

    doBoxesOverlap = (box1, box2) => {
        return !(box1.x + box1.width < box2.x || box2.x + box2.width < box1.x || box1.y + box1.height < box2.y || box2.y + box2.height < box1.y);
    };

    drawBox = (canvas: HTMLCanvasElement, x: number, y: number, boxHeight: number = 230) => {
        const ctx = canvas.getContext('2d');
        const boxWidth = 400;
        ctx.beginPath();
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.fillRect(x, y, boxWidth, boxHeight);
        ctx.rect(x, y, boxWidth, boxHeight);
        ctx.stroke();
    };

    drawCustomInfoBox = (canvas: HTMLCanvasElement, x: number, y: number, label1: string, value1: string, label2?: string, value2?: string) => {
        this.drawBox(canvas, x, y);
        const ctx = canvas.getContext('2d');
        const padding = 20;
        const labelGap = 15;
        const labelColor = '#2D343A';

        const valueOpts = {
            x: x + padding + 5,
            width: 350,
            height: 70,
            fontSize: 22,
            align: 'left',
            vAlign: 'top',
            justify: false,
        };
        const labelOpts = {
            x: x + padding + 5,
            width: 350,
            height: 30,
            fontSize: 20,
            align: 'left',
            vAlign: 'top',
            justify: false,
        };

        ctx.fillStyle = labelColor;
        const label1Stats = canvasTxt.drawText(ctx, label1, {
            ...(labelOpts as any),
            y: y + padding,
        });
        ctx.fillStyle = 'black';
        const value1y = y + padding + label1Stats.height + labelGap;
        const value1Stats = canvasTxt.drawText(ctx, value1, {
            ...(valueOpts as any),
            y: value1y,
        });

        if (label2) {
            ctx.fillStyle = labelColor;
            const label2y = value1y + value1Stats.height + 20;
            const label2Stats = canvasTxt.drawText(ctx, label2, {
                ...(labelOpts as any),
                y: label2y,
            });
            ctx.fillStyle = 'black';
            canvasTxt.drawText(ctx, value2, {
                ...(valueOpts as any),
                y: label2y + label2Stats.height + labelGap,
            });
        }
    };

    drawApprovedBox = (canvas: HTMLCanvasElement, x: number, y: number, user: Partial<IUser>) => {
        this.drawBox(canvas, x, y);
        const ctx = canvas.getContext('2d');
        const padding = 20;

        const dateString = this.includeTime ? new Date(this.exportDateMs).toLocaleString() : new Date(this.exportDateMs).toLocaleDateString();
        drawText(ctx, __('general.export_date'), x + padding, y + padding + 20, 20, '#2D343A');
        drawText(ctx, dateString, x + padding, y + padding + 50, 24);

        drawText(ctx, __('general.exported_by'), x + padding, y + padding + 85, 20, '#2D343A');
        drawText(ctx, UsersService.getFullName(user), x + padding, y + padding + 115, 24);
        drawText(ctx, __('js_map.image_export.approved_by'), x + padding, y + padding + 145, 20, '#2D343A');
    };

    drawScaleDateBox = (canvas: HTMLCanvasElement, x: number, y: number, project: Partial<IProject>) => {
        this.drawBox(canvas, x, y);
        const ctx = canvas.getContext('2d');
        const padding = 20;

        if (!this.includeScaleLine) {
            drawText(ctx, __('js_map.image_export.coord_height_system'), x + padding, y + padding + 20, 20, '#2D343A');
            drawText(ctx, project.coordinateSystem.name, x + padding + 20, y + padding + 60, 24);
            drawText(ctx, project.heightSystem?.name, x + padding + 20, y + padding + 90, 24);
        } else {
            drawText(ctx, __('general.location'), x + padding, y + padding + 20, 20, '#2D343A');
            drawText(ctx, __('js_map.scale'), x + padding, y + padding + 55, 20, '#2D343A');
            drawText(ctx, '1:' + this.scale.toFixed(0), x + padding + 110, y + padding + 55, 24);
            const paperSizeMetrics = drawText(ctx, __('js_map.papersize'), x + padding, y + padding + 90, 20, '#2D343A');
            drawText(ctx, this.paperLabel, Math.max(x + padding + paperSizeMetrics.width + 20, x + padding + 110), y + padding + 90, 24);
            drawText(ctx, __('js_map.image_export.coord_height_system'), x + padding, y + padding + 130, 20, '#2D343A');
            drawText(ctx, project.coordinateSystem.name, x + padding + 20, y + padding + 165, 24);
            drawText(ctx, project.heightSystem?.name, x + padding + 20, y + padding + 195, 24);
        }
    };

    drawLogpointsBox = (canvas: HTMLCanvasElement, x: number, y: number) => {
        this.drawBox(canvas, x, y, 400);
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'black';
        const legendStartX = x + 70;
        let lineSpacing = 44;
        ctx.font = '20px Arial, Helvetica, sans-serif';
        ctx.fillText(__('logpoints.type.ROVER_GPS_js') + ' / ' + __('logpoints.type.SIMULATOR_js'), legendStartX, y + lineSpacing - 2);
        ctx.drawImage(LogpointFeatureFactory.renderIconCanvas({ type: 'rover' } as any, 15, 'red', false), legendStartX - 50, y + 20);

        ctx.fillText(__('logpoints.type.VEHICLE_js'), legendStartX, y + lineSpacing * 2);
        ctx.drawImage(LogpointFeatureFactory.renderIconCanvas({ type: 'vehicle' } as any, 15, 'red', false), legendStartX - 50, y + 65);

        ctx.fillText(__('logpoints.type.TOTAL_STATION_js'), legendStartX, y + lineSpacing * 3);
        ctx.drawImage(LogpointFeatureFactory.renderIconCanvas({ type: 'total_station' } as any, 15, 'red', false), legendStartX - 50, y + 110);

        const colorStartY = y + lineSpacing * 4;
        drawText(ctx, __('js_logpoints.logpoints_color'), legendStartX - 40, colorStartY + 10, 20, '#2D343A');
        lineSpacing = 35;
        const colorDesc = LogpointsService.colorAndDescription;
        if (this.props.logpointsColorMode === 'timestamp_color_ramp') {
            drawColorSwatch(ctx, colorDesc.newer[1], legendStartX - 40, colorStartY + lineSpacing, colorDesc.newer[0]);
            drawColorSwatch(ctx, colorDesc.older[1], legendStartX - 40, colorStartY + lineSpacing * 2, colorDesc.older[0]);
        } else if (this.props.logpointsColorMode === 'tolerance' || this.props.logpointsColorMode === 'tolerance_approval') {
            drawColorSwatch(ctx, colorDesc.in_tolerance[1], legendStartX - 40, colorStartY + lineSpacing, colorDesc.in_tolerance[0]);
            drawColorSwatch(ctx, colorDesc.under_tolerance[1], legendStartX - 40, colorStartY + lineSpacing * 2, colorDesc.under_tolerance[0]);
            drawColorSwatch(ctx, colorDesc.not_in_tolerance[1], legendStartX - 40, colorStartY + lineSpacing * 3, colorDesc.not_in_tolerance[0]);

            if (this.props.logpointsColorMode === 'tolerance_approval') {
                drawColorSwatch(ctx, colorDesc.approved[1], legendStartX - 40, colorStartY + lineSpacing * 4, colorDesc.approved[0]);
                drawColorSwatch(ctx, colorDesc.checked[1], legendStartX - 40, colorStartY + lineSpacing * 5, colorDesc.checked[0]);
            }
        }
    };

    saveSettings = () => {
        LStorage.setItem(this.storageKey, {
            customFields: this.customFields,
            includeTime: this.includeTime,
            includeModelNames: this.includeModelNames,
            includeLegend: this.includeLegend,
            legendMode: this.legendMode,
            scale: this.overlayScale,
        });
    };

    get effectiveOverlayScale() {
        return this.overlayScale;
    }

    drawMassCalculationBox = (canvas: HTMLCanvasElement, x: number, y: number) => {
        if (!measurementStore) {
            return;
        }
        const boxHeight = 400;
        this.drawBox(canvas, x, y, boxHeight);
        const ctx = canvas.getContext('2d');
        const padding = 30;
        const measurements = measurementStore.listVisibleMeasurements();
        if (measurements.length === 0) return;

        const selectedMeasurement = measurementStore.getSelectedMeasurement() ?? measurements[0];
        const lineHeight = 30;
        let currentY = y + padding + 20;

        const fillVal = selectedMeasurement.fill_volume?.toFixed(2) ?? '0';
        const cutVal = selectedMeasurement.cut_volume?.toFixed(2) ?? '0';
        const netVal = (selectedMeasurement.fill_volume - selectedMeasurement.cut_volume).toFixed(2);
        const totalVal = (selectedMeasurement.fill_volume + selectedMeasurement.cut_volume).toFixed(2);

        if (selectedMeasurement.name) {
            drawText(ctx, selectedMeasurement.name, x + padding, currentY, 24);
            currentY += lineHeight + 20;
        }
        drawText(ctx, `${__('js_map_measuring_tools.fill')}: ${fillVal} m³`, x + padding, currentY, 20);
        currentY += lineHeight;
        drawText(ctx, `${__('js_map_measuring_tools.cut')}: ${cutVal} m³`, x + padding, currentY, 20);
        currentY += lineHeight;
        drawText(ctx, `${__('js_map_measuring_tools.net')}: ${netVal} m³`, x + padding, currentY, 20);
        currentY += lineHeight;
        drawText(ctx, `${__('js_map_measuring_tools.total')}: ${totalVal} m³`, x + padding, currentY, 20);

        const legendGradient: HTMLCanvasElement = document.querySelector('[data-measurement-uuid="' + selectedMeasurement.uuid + '"] .color-legend-container canvas');
        if (legendGradient) {
            const maxHeight = 380;
            const scale = legendGradient.height > maxHeight ? maxHeight / legendGradient.height : 1;
            const scaledWidth = legendGradient.width * scale;
            ctx.drawImage(legendGradient, ctx.canvas.width - scaledWidth + 10, y + 10, scaledWidth, maxHeight);
        }
    };
}

function drawText(ctx, text: string, x, y, fontSize: number, color: string = 'black', stroke = false, bg = false): TextMetrics {
    if (fontSize) {
        ctx.font = fontSize + 'px Arial, Helvetica, sans-serif';
    }

    const metrics = ctx.measureText(text);
    if (bg) {
        const textHeight = fontSize || parseInt(ctx.font.match(/\d+px/)[0], 10);
        ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillRect(x - 3, y - textHeight + 3, metrics.width + 6, textHeight + 6);
    }

    if (stroke) {
        ctx.lineJoin = 'round';
        ctx.strokeStyle = 'rgba(255,255,255,0.4)';
        ctx.lineWidth = 10;
        ctx.strokeText(text, x, y);
    }
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);

    return metrics;
}

function drawColorSwatch(ctx, text: string, x, y, color) {
    ctx.fillStyle = color;
    ctx.fillRect(x, y, 20, 20);
    drawText(ctx, text, x + 40, y + 18, 20, '#2D343A');
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x, y, maxWidth: number, lineHeight: number, bg = false) {
    if (!text) return;
    text = text.replace(/<sup>/g, '').replace(/<\/sup>/g, '');
    text = text.replace(/<span.*?>(.*?)<\/span>/gi, '$1');

    const lines = text
        .split('<br>') // map labels use <br> to split lines
        .map(line => line?.trim());

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
        const currentLine = lines[lineIndex];
        var words = lines.length > 1 && currentLine.includes(' ') ? currentLine.split(' ') : currentLine.split('/'); // dont split words on multiline text
        var line = '';

        for (var n = 0; n < words.length; n++) {
            var testLine = line + words[n] + ' ';
            var metrics = ctx.measureText(testLine);
            var testWidth = metrics.width;
            if (testWidth > maxWidth && n > 0) {
                drawText(ctx, line, x, y, null, 'black', true, bg);
                line = words[n] + ' ';
                y += lineHeight;
            } else {
                line = testLine;
            }
        }
        drawText(ctx, line, x, y, null, 'black', true, bg);

        if (lineIndex < lines.length - 1) {
            y += lineHeight;
        }
    }
}
